import { Action, KeyedAction, Store } from '@openfin/predux';
import { createSelector } from 'reselect';

import { NotificationModifiers } from '../../model/NotificationModifiers';
import { StoredNotification } from '../../model/StoredNotification';
import { RootState } from '../State';
import { RemoveNotifications } from '../Actions';
import type { TemplateMarkdown } from '../../../client/templates/templates';

/**
 * Offline sorted notification by categories.
 * All notifications object maps ordered and insertion sorted by Date.
 */
export interface SortedNotificationsState {
  byId: Record<string, StoredNotification>;

  byApplication: {
    [uuid: string]: Record<string, StoredNotification>;
  };

  byStream: {
    [streamId: string]: Record<string, StoredNotification>;
  };
}

export const initialSortedNotificationsState: SortedNotificationsState = {
  byId: {},
  byApplication: {},
  byStream: {},
};

export class SubmitNotificationForm extends KeyedAction<RootState, 'notifications'> {
  constructor(
    public readonly notification: StoredNotification<TemplateMarkdown>,
    public readonly form: Record<string, unknown>
  ) {
    super('notifications');
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    await complete();
    await store.dispatch(new RemoveNotifications([this.notification]));
  }
}

export class ApplyNotificationModifier extends KeyedAction<RootState, 'notifications'> {
  public readonly notification: StoredNotification;

  constructor(
    notification: StoredNotification,
    public readonly modifiers: Partial<NotificationModifiers>
  ) {
    super('notifications');
    this.notification = { ...notification, modifiers: { ...modifiers } };
  }

  public reduce(state: RootState['notifications']): RootState['notifications'] {
    const { id } = this.notification;
    const newNotificationList = [...state];
    const index = [...state].findIndex((n) => n.id === id);
    const modifiedNotification: StoredNotification = {
      ...newNotificationList[index],
      modifiers: this.modifiers,
    };
    newNotificationList[index] = modifiedNotification;

    return newNotificationList;
  }
}

export class MinimizeToast extends Action<RootState> {
  constructor(public readonly notification: StoredNotification) {
    super();
  }

  public async call(store: Store<RootState>, complete: () => Promise<void>): Promise<void> {
    if (this.notification.notification.sticky === 'sticky') {
      await store.dispatch(new ApplyNotificationModifier(this.notification, { notSticky: true }));
    }
    await complete();
  }

  public reduce(state: RootState): RootState {
    const { notification } = this;
    if (notification.notification.sticky === 'sticky') {
      return {
        ...state,
        stickyNotifications: [...state.stickyNotifications.filter((x) => x.id !== notification.id)],
      };
    }

    return state;
  }
}

export const getNotifications = (state: RootState): StoredNotification[] => state.notifications;
export const getStickyNotifications = createSelector(getNotifications, (notifitications) =>
  notifitications.filter((n) => n.notification.sticky === 'sticky' && !n.modifiers?.notSticky)
);
